import React, { useState, useEffect, useContext } from "react";
import {
  Users,
  Heart,
  Calendar,
  UserCheck,
  AlertCircle,
  CheckCircle,
  Clock,
  MoreVertical,
  Plus,
  FileText,
  BarChart3,
  ArrowUp,
  ArrowDown,
  IndianRupee,
} from "lucide-react";
import { AuthContext } from "../../../context/AuthContext";
import { Link } from "react-router-dom";

import { getAllUser, getUserById } from "../../../services/api";
import AdminReportGenerator from "./AdminReportGenerator";

const AdminDashboard = () => {
  const {
    loginUser,
    getAllPet,
    allPets,
    allPetsLoading,

    getAppointments,
    allAppointments,
    allAppointmentsLoading,

    getDoctors,
    doctors,
    doctorLoading,
  } = useContext(AuthContext);

  const [showReport, setShowReport] = useState(false);
  const [allUsersData, setAllUsersData] = useState([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    role: "",
    status: "",
    joinedDate: "",
    location: "",
  });

  const [selectedTimeRange, setSelectedTimeRange] = useState("today");
  const [dashboardStats, setDashboardStats] = useState({
    totalPets: 0,
    totalAppointments: 0,
    totalDoctors: 0,
    totalUsers: 0,
    pendingAppointments: 0,
    completedAppointments: 0,
    cancelledAppointments: 0,
    revenue: 0,
    revenueGrowth: 0,
  });

  // Fetch all users from API (only once)
  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all users at once
      const response = await getAllUser({ limit: 1000 });

      // Handle the response structure
      let usersData = [];
      if (response.success && response.data) {
        usersData = response.data;
      } else {
        // Fallback for different response structures
        usersData = response.users || response.data || response;
        usersData = Array.isArray(usersData) ? usersData : [];
      }

      console.log("Fetched users:", usersData.length);
      setAllUsersData(usersData);
      setFilteredUsers(usersData);
      setIsInitialLoad(false);
    } catch (err) {
      setError(err.message || "Failed to fetch users");
      setAllUsersData([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering function
  const applyFilters = () => {
    console.log("Applying filters:", {
      filters,
      searchTerm: debouncedSearchTerm,
    });

    let filtered = [...allUsersData];

    // Apply search filter
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase().trim();
      filtered = filtered.filter((user) => {
        const name =
          user.name ||
          (user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.firstName) ||
          "";
        const email = user.email || "";
        const phone = user.phone || "";
        const role = user.role || "";

        return (
          name.toLowerCase().includes(searchLower) ||
          email.toLowerCase().includes(searchLower) ||
          phone.toLowerCase().includes(searchLower) ||
          role.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply role filter
    if (filters.role) {
      filtered = filtered.filter(
        (user) => user.role?.toLowerCase() === filters.role.toLowerCase()
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter((user) => {
        const userStatus = user.status || "active"; // Default to active if no status
        return userStatus.toLowerCase() === filters.status.toLowerCase();
      });
    }

    // Apply joined date filter
    if (filters.joinedDate) {
      filtered = filtered.filter((user) => {
        const userDate = user.createdAt || user.joinedDate;
        if (!userDate) return false;

        const userDateOnly = new Date(userDate).toISOString().split("T")[0];
        return userDateOnly === filters.joinedDate;
      });
    }

    // Apply location filter
    if (filters.location.trim()) {
      const locationLower = filters.location.toLowerCase().trim();
      filtered = filtered.filter((user) => {
        const address = user.address || user.location || "";
        return address.toLowerCase().includes(locationLower);
      });
    }

    console.log(
      "Filtered users:",
      filtered.length,
      "out of",
      allUsersData.length
    );
    setFilteredUsers(filtered);
  };

  // Mock data for recent activities and notifications
  const recentActivities = [
    {
      id: 1,
      type: "appointment",
      message: "New appointment booked for Buddy",
      time: "2 minutes ago",
      icon: Calendar,
      color: "text-blue-600",
    },
    {
      id: 2,
      type: "pet",
      message: "New pet registered: Whiskers",
      time: "15 minutes ago",
      icon: Heart,
      color: "text-green-600",
    },
    {
      id: 3,
      type: "user",
      message: "New user registration: John Doe",
      time: "1 hour ago",
      icon: Users,
      color: "text-purple-600",
    },
    {
      id: 4,
      type: "appointment",
      message: "Appointment completed for Charlie",
      time: "2 hours ago",
      icon: CheckCircle,
      color: "text-green-600",
    },
    {
      id: 5,
      type: "alert",
      message: "Low medication stock alert",
      time: "3 hours ago",
      icon: AlertCircle,
      color: "text-red-600",
    },
  ];

  const notifications = [
    {
      id: 1,
      title: "System Maintenance",
      message: "Scheduled maintenance tonight at 2:00 AM",
      type: "info",
      urgent: false,
      time: "1 hour ago",
    },
    {
      id: 2,
      title: "Payment Failed",
      message: "Payment failed for appointment #12345",
      type: "error",
      urgent: true,
      time: "2 hours ago",
    },
    {
      id: 3,
      title: "New Doctor Registration",
      message: "Dr. Sarah Johnson has requested to join",
      type: "success",
      urgent: false,
      time: "3 hours ago",
    },
  ];

  // Fetch data on component mount
  useEffect(() => {
    fetchAllUsers();
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch all pets
      await getAllPet({ limit: 1000 });

      // Fetch all appointments
      await getAppointments({ limit: 1000 });

      // Fetch all doctors
      await getDoctors({ limit: 1000 });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  // Calculate dashboard statistics
  useEffect(() => {
    if (allAppointments?.length > 0) {
      const pendingAppointments = allAppointments.filter(
        (apt) => apt.status === "pending"
      ).length;
      const completedAppointments = allAppointments.filter(
        (apt) => apt.status === "completed"
      ).length;
      const cancelledAppointments = allAppointments.filter(
        (apt) => apt.status === "cancelled"
      ).length;

      setDashboardStats({
        totalPets: allPets?.length,
        totalAppointments: allAppointments.length,
        totalDoctors: doctors?.length,
        totalUsers: allUsersData.length, // ✅ Use real user count instead of mock data
        pendingAppointments,
        completedAppointments,
        cancelledAppointments,
        revenue: 12500, // Mock data
      });
    }
  }, [allPets, allAppointments, doctors, allUsersData]);

  const getAppointmentStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getOwnerName = (item) => {
    // Debug: Log the item structure to console
    console.log("Item structure:", item);
    console.log("Pet data:", item.pets || item);
    console.log(
      "Owner data:",
      item.pets?.ownerId || item.ownerId || item.owner
    );

    // Try different possible data structures
    const ownerData =
      item.pets?.ownerId || item.ownerId || item.pets?.owner || item.owner;

    if (!ownerData) {
      console.log("No owner data found");
      return "Unknown Owner";
    }

    // Try userDetails first (most common structure)
    if (ownerData.userDetails) {
      const userDetails = ownerData.userDetails;
      if (userDetails.name) return userDetails.name;
      if (userDetails.firstName) {
        return (
          userDetails.firstName +
          (userDetails.lastName ? " " + userDetails.lastName : "")
        );
      }
      if (userDetails.email) return userDetails.email;
    }

    // Try direct owner properties
    if (ownerData.name) return ownerData.name;
    if (ownerData.firstName) {
      return (
        ownerData.firstName +
        (ownerData.lastName ? " " + ownerData.lastName : "")
      );
    }
    if (ownerData.email) return ownerData.email;

    console.log("No valid owner name found in:", ownerData);
    return "Unknown Owner";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const StatCard = ({ title, value, icon: Icon, color, growth, loading }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">
            {loading ? (
              <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
            ) : (
              value
            )}
          </p>
          {growth !== undefined && (
            <p className="text-sm text-gray-500 mt-1 flex items-center">
              {growth > 0 ? (
                <ArrowUp className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <ArrowDown className="w-4 h-4 text-red-500 mr-1" />
              )}
              {Math.abs(growth)}% from last month
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (allPetsLoading && allAppointmentsLoading && doctorLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading admin dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-1">
                Welcome back, {loginUser?.name || "Administrator"}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
              </select>
              <button
                onClick={() => setShowReport(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <BarChart3 className="w-5 h-5" />
                <span>Generate Report</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <StatCard
            title="Total Pets"
            value={dashboardStats.totalPets}
            icon={Heart}
            color="bg-pink-500"
            loading={allPetsLoading}
          />
          <StatCard
            title="Total Appointments"
            value={dashboardStats.totalAppointments}
            icon={Calendar}
            color="bg-blue-500"
            loading={allAppointmentsLoading}
          />
          <StatCard
            title="Active Doctors"
            value={dashboardStats.totalDoctors}
            icon={UserCheck}
            color="bg-green-500"
            loading={doctorLoading}
          />
          <StatCard
            title="Total Users"
            value={allUsersData.length}
            icon={Users}
            color="bg-purple-500"
            loading={false}
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <StatCard
            title="Pending Appointments"
            value={dashboardStats.pendingAppointments}
            icon={Clock}
            color="bg-yellow-500"
            loading={allAppointmentsLoading}
          />
          <StatCard
            title="Completed Today"
            value={dashboardStats.completedAppointments}
            icon={CheckCircle}
            color="bg-green-500"
            loading={allAppointmentsLoading}
          />
          <StatCard
            title="Monthly Revenue"
            value={`₹${dashboardStats.revenue.toLocaleString()}`}
            icon={IndianRupee}
            color="bg-emerald-500"
            loading={false}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recent Appointments */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              {/* <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Recent Appointments
                </h2>
                <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  View All
                </button>
              </div> */}

              {/* Recent Appoinements*/}
              <div className="bg-white rounded-lg shadow-sm py-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    See All Pets
                  </h2>
                  {/* <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                    View All
                  </button> */}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Array.isArray(allPets) &&
                    allPets?.slice(0, allPets?.length).map((pet) => (
                      <div
                        key={pet._id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-xl">
                              {pet.image ? (
                                <img src={pet.image} className="rounded-full" />
                              ) : (
                                "🐕"
                              )}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">
                              {pet.name || "Unnamed Pet"}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {pet.breed || "Unknown breed"} •{" "}
                              {(() => {
                                if (!pet.dateOfBirth) return "Unknown age";

                                const today = new Date();
                                const birthDate = new Date(pet.dateOfBirth);

                                // Difference in ms → days
                                const diffMs = today - birthDate;
                                const diffDays = Math.floor(
                                  diffMs / (1000 * 60 * 60 * 24)
                                );
                                const diffMonths =
                                  today.getMonth() -
                                  birthDate.getMonth() +
                                  12 *
                                    (today.getFullYear() -
                                      birthDate.getFullYear());
                                const diffYears =
                                  today.getFullYear() - birthDate.getFullYear();

                                // Adjust years correctly
                                let ageYears = diffYears;
                                if (
                                  today.getMonth() < birthDate.getMonth() ||
                                  (today.getMonth() === birthDate.getMonth() &&
                                    today.getDate() < birthDate.getDate())
                                ) {
                                  ageYears--;
                                }

                                if (ageYears >= 1) {
                                  return `${ageYears} year${
                                    ageYears > 1 ? "s" : ""
                                  }`;
                                } else if (diffMonths >= 1) {
                                  return `${diffMonths} month${
                                    diffMonths > 1 ? "s" : ""
                                  }`;
                                } else {
                                  return `${diffDays} day${
                                    diffDays !== 1 ? "s" : ""
                                  }`;
                                }
                              })()}
                            </p>

                            <p className="text-xs text-gray-500">
                              Owner Name: {pet.ownerId.userDetails.name}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Quick Actions
              </h2>
              <div className="space-y-3">
                <Link to={"/dashboard/add-doctor"}>
                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                    <Plus className="w-4 h-4" />
                    <span>Add New Doctor</span>
                  </button>
                </Link>
                {/* <button className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                  <Calendar className="w-4 h-4" />
                  <span>Schedule Appointment</span>
                </button> */}
                <button
                  onClick={() => setShowReport(true)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  <span>Generate Report</span>
                </button>
                {/* <button className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                  <Settings className="w-4 h-4" />
                  <span>System Settings</span>
                </button> */}
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Notifications
                </h2>
                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                  {notifications?.filter((n) => n.urgent).length} urgent
                </span>
              </div>
              <div className="space-y-3">
                {notifications?.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-3 rounded-lg border-l-4 ${
                      notification.urgent
                        ? "bg-red-50 border-l-red-500"
                        : "bg-blue-50 border-l-blue-500"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {notification.title}
                        </h4>
                        <p className="text-xs text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {notification.time}
                        </p>
                      </div>
                      <button className="text-gray-400 hover:text-gray-600">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activities */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Recent Activities
              </h2>
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg bg-gray-100`}>
                      <activity.icon className={`w-4 h-4 ${activity.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">
                        {activity.message}
                      </p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <AdminReportGenerator
        isOpen={showReport}
        onClose={() => setShowReport(false)}
        adminData={loginUser}
        dashboardStats={dashboardStats}
        allPets={allPets}
        allAppointments={allAppointments}
        doctors={doctors}
        allUsersData={allUsersData}
      />
    </div>
  );
};

export default AdminDashboard;
